import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/content/defaults";

// Deep-merge helper: server overrides extend defaults (server is partial)
const deepMerge = (target, source) => {
  if (!source) return target;
  if (Array.isArray(source)) return source; // arrays replaced wholesale
  if (typeof source !== "object") return source;
  const out = { ...(target || {}) };
  for (const k of Object.keys(source)) {
    out[k] = deepMerge(target ? target[k] : undefined, source[k]);
  }
  return out;
};

const SiteContentContext = createContext({ content: DEFAULT_CONTENT, refresh: () => {}, loading: true });

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/site-content");
      const merged = deepMerge(DEFAULT_CONTENT, data || {});
      setContent(merged);
    } catch {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply colors as CSS variables on document root
  useEffect(() => {
    const root = document.documentElement;
    const c = content.colors || {};
    Object.entries(c).forEach(([k, v]) => {
      if (typeof v === "string" && v) root.style.setProperty(`--${k}`, v);
    });
  }, [content.colors]);

  return (
    <SiteContentContext.Provider value={{ content, refresh, loading }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => useContext(SiteContentContext);
